/**
 * Enhanced email validation to catch fake/invalid emails
 * Validates email format and checks for suspicious patterns
 * NOTE: Real email verification (checking if email actually exists) is done separately
 */

/**
 * List of known disposable/temporary email domains
 * This is a basic list - real email verification API will catch more
 */
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  '10minutemail.com',
  'tempmail.com',
  'guerrillamail.com',
  'mailinator.com',
  'throwaway.email',
  'temp-mail.org',
  'getnada.com',
  'mohmal.com',
  'fakeinbox.com',
  'trashmail.com',
  // Add more as needed
]);

/**
 * Enhanced email validation (format only - real verification is done separately)
 * Checks for:
 * 1. Basic email format
 * 2. Consecutive dots (invalid)
 * 3. Disposable email domains
 * 4. Reasonable length limits
 */
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmed = email.trim().toLowerCase();

  // Basic length checks
  if (trimmed.length < 5) {
    return { isValid: false, error: 'Email is too short' };
  }
  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }

  // Basic format check
  const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicEmailRegex.test(trimmed)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Split into local and domain parts
  const [localPart, domain] = trimmed.split('@');
  
  if (!localPart || !domain) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Check local part length (before @)
  if (localPart.length > 64) {
    return { isValid: false, error: 'Email local part is too long' };
  }

  // Check for consecutive dots (invalid in any email)
  if (localPart.includes('..') || domain.includes('..')) {
    return { isValid: false, error: 'Email contains invalid characters' };
  }

  // Check for disposable email domains
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
    return { isValid: false, error: 'Disposable email addresses are not allowed. Please use a real email address.' };
  }

  // Check domain has valid TLD (at least 2 characters after last dot)
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return { isValid: false, error: 'Invalid email domain' };
  }
  
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return { isValid: false, error: 'Invalid email domain' };
  }

  // Check for valid characters only
  const validEmailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (!validEmailRegex.test(trimmed)) {
    return { isValid: false, error: 'Email contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Check if email is from a disposable/temporary email service
 */
export function isDisposableEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? DISPOSABLE_EMAIL_DOMAINS.has(domain) : false;
}
